'use client';

import { WalletListItem, WalletListItemProps } from './WalletsListItem';
import {
  useClientLocale,
  Locale,
  useClientTranslation,
} from '@bts-web/utils-lokalise';
import { WalletListItemSkeleton } from './WalletListItemSkeleton';
import { NewWalletAddressItem } from './NewWalletAddressItem';
import { InvalidFormat } from './InvalidFormat';
import { filterInput, isSearchValid } from './utils';
import { ErrorLoadingList } from './ErrorLoadingList';
import { useExternalCryptoAddresses } from '../../hooks/useExternalCryptoAddresses';
import { TCryptoTransferTranslations } from '../../../../utils/getCryptoTransferTranslations';
import { type TimeScalar } from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import { TTransfersNextStepStateWithActions } from '../../../../utils/useTransfersNextStepState.client';
import { TransfersCommonStepsIds } from '../../../../types';

export type WalletAddressItem = Omit<
  WalletListItemProps,
  'onClick' | 'selected'
>;

interface WalletListProps {
  onChooseItem: (walletAddress: WalletAddressItem) => void;
  onCreateNewWalletAddress: (type: 'address' | 'name') => void;
  selectedItemAddress: string | null;
  searchInput: string | null;
  translations: TCryptoTransferTranslations;
  state: TTransfersNextStepStateWithActions;
  hasOneNetwork?: boolean;
}

export const WalletsList = ({
  selectedItemAddress,
  searchInput,
  translations,
  onChooseItem,
  onCreateNewWalletAddress,
  state,
  hasOneNetwork,
}: WalletListProps) => {
  const { t } = useClientTranslation();

  const currentLocale = useClientLocale();

  const { items, loading, error, fetchWalletLists } =
    useExternalCryptoAddresses({
      assetId: state.selectedAssetData?.id,
      onEmptyList: () => {
        handleSkipRecipientStep();
      },
    });

  const handleSkipRecipientStep = () => {
    state.changeToPageAndBindActions({
      pageId: hasOneNetwork
        ? TransfersCommonStepsIds.AssetAndNetworkSummary
        : TransfersCommonStepsIds.ChooseNetwork,
      newBackAction: {
        pageId: TransfersCommonStepsIds.ChooseAsset,
        type: 'page',
      },
    });
  };

  const handleTryAgain = () => {
    fetchWalletLists();
  };

  const handleCreatedOnTranslation = (
    locale: Locale,
    createdAtDate: TimeScalar,
  ): string => {
    return t('created_on_date', {
      date: convertToLocaleDateString(createdAtDate, locale),
    });
  };

  let filteredItems = items ?? [];

  if (searchInput) {
    filteredItems = items.filter(filterInput(searchInput) ?? []);
  }

  if (loading) {
    return <WalletsListSkeleton />;
  }

  if (error) {
    return (
      <ErrorLoadingList
        error={error}
        onTryAgain={handleTryAgain}
        translations={translations}
      />
    );
  }

  if (!filteredItems?.length && searchInput && !isSearchValid(searchInput)) {
    return <InvalidFormat translations={translations} />;
  }

  if (!filteredItems?.length && searchInput) {
    return (
      <NewWalletAddressItem
        newWalletAddress={searchInput}
        translations={translations}
        onClick={onCreateNewWalletAddress}
      />
    );
  }

  return (
    <ul>
      {filteredItems.map((item) => (
        <li key={item.id}>
          <WalletListItem
            id={item.id}
            walletName={item.walletName}
            address={item.address}
            assetLogoUrl={item.assetLogoUrl}
            networkLogoUrl={item.networkLogoUrl}
            createdAt={item.createdAt}
            onClick={() => onChooseItem(item)}
            selected={selectedItemAddress === item.address}
            currentLocale={currentLocale}
            createdOnTranslation={handleCreatedOnTranslation}
            translations={translations}
          />
        </li>
      ))}
    </ul>
  );
};

export const WalletsListSkeleton = () => (
  <ul>
    <li>
      <WalletListItemSkeleton />
    </li>
    <li>
      <WalletListItemSkeleton />
    </li>
    <li>
      <WalletListItemSkeleton />
    </li>
    <li>
      <WalletListItemSkeleton />
    </li>
    <li>
      <WalletListItemSkeleton />
    </li>
  </ul>
);
